import React, { useEffect, useState } from "react";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import StaffLayout from "./StaffLayout";
import "./AnalyticsDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value || 0));

const AnalyticsDashboard = () => {
  const [memberData, setMemberData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [massagePayoutData, setMassagePayoutData] = useState(null);
  const [communicationData, setCommunicationData] = useState(null);
  const [interactionData, setInteractionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [providerId, setProviderId] = useState("");
  const [activeTab, setActiveTab] = useState("bookings");
  const [exportType, setExportType] = useState("bookings");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = { period };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (providerId && activeTab === "massagePayouts") {
          params.providerId = providerId;
        }

        let response;
        switch (activeTab) {
          case "members":
            response = await staffAPI.getMemberAnalytics(params);
            setMemberData(response.data);
            break;
          case "accounts":
            response = await staffAPI.getAccountAnalytics(params);
            setAccountData(response.data);
            break;
          case "bookings":
            response = await staffAPI.getBookingAnalytics(params);
            setBookingData(response.data);
            break;
          case "massagePayouts":
            response = await staffAPI.getMassagePayoutAnalytics(params);
            setMassagePayoutData(response.data);
            break;
          case "communications":
            response = await staffAPI.getCommunicationAnalytics(params);
            setCommunicationData(response.data);
            break;
          case "interactions":
            response = await staffAPI.getInteractionAnalytics(params);
            setInteractionData(response.data);
            break;
          default:
            break;
        }
      } catch (err) {
        handleApiError(
          err,
          "AnalyticsDashboard",
          setError,
          "Failed to load analytics data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, period, startDate, endDate, providerId]);

  useEffect(() => {
    setExportType(activeTab);
  }, [activeTab]);

  const handleExport = async () => {
    try {
      const params = {
        type: exportType,
        period,
        startDate,
        endDate,
      };

      if (exportType === "massagePayouts" && providerId) {
        params.providerId = providerId;
      }

      const response = await staffAPI.exportAnalytics(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportType}-analytics.csv`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      handleApiError(
        err,
        "AnalyticsDashboard.export",
        setError,
        "Failed to export analytics data. Please try again later."
      );
    }
  };

  const renderMemberGrowthChart = () => {
    if (!memberData || !memberData.memberGrowth) return null;

    const chartData = {
      labels: memberData.memberGrowth.map((item) => item.date),
      datasets: [
        {
          label: "New Members",
          data: memberData.memberGrowth.map((item) => item.newMembers),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          tension: 0.1,
        },
        {
          label: "Total Members",
          data: memberData.memberGrowth.map((item) => item.totalMembers),
          borderColor: "rgba(54, 162, 235, 1)",
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          tension: 0.1,
        },
      ],
    };

    return (
      <div className="chart-container">
        <Line
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "top" },
              title: { display: true, text: "Member Growth Over Time" },
            },
            scales: { y: { beginAtZero: true } },
          }}
        />
      </div>
    );
  };

  const renderAccountAnalyticsChart = () => {
    if (!accountData || !accountData.roles) return null;

    const labels = accountData.roles.map((role) =>
      role.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    );

    const totals = accountData.roles.map(
      (role) => accountData.totals?.[role] || 0
    );

    const newCounts = accountData.roles.map(
      (role) => accountData.newInRange?.[role] || 0
    );

    const chartData = {
      labels,
      datasets: [
        {
          label: "Total Accounts",
          data: totals,
          backgroundColor: "rgba(176, 141, 97, 0.7)",
        },
        {
          label: "New in Range",
          data: newCounts,
          backgroundColor: "rgba(31, 92, 99, 0.6)",
        },
      ],
    };

    return (
      <div className="chart-container">
        <div className="summary-stats">
          {accountData.roles.map((role) => (
            <div key={role} className="stat-card">
              <h3>{role.replace(/_/g, " ")}</h3>
              <p>{accountData.totals?.[role] || 0}</p>
              <p className="stat-subtext">
                New: {accountData.newInRange?.[role] || 0}
              </p>
            </div>
          ))}
        </div>
        <div className="chart-row">
          <div className="chart-col chart-col-wide">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Account Types" },
                },
                scales: { y: { beginAtZero: true } },
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderBookingAnalyticsChart = () => {
    if (!bookingData || !bookingData.bookings) return null;

    const statusChartData = {
      labels: bookingData.bookings.map((item) => item.date),
      datasets: [
        {
          label: "Booked",
          data: bookingData.bookings.map((item) => item.booked),
          backgroundColor: "rgba(31, 92, 99, 0.65)",
        },
        {
          label: "Completed",
          data: bookingData.bookings.map((item) => item.completed),
          backgroundColor: "rgba(87, 163, 96, 0.72)",
        },
        {
          label: "Cancelled",
          data: bookingData.bookings.map((item) => item.cancelled),
          backgroundColor: "rgba(217, 119, 6, 0.72)",
        },
        {
          label: "No-Show",
          data: bookingData.bookings.map((item) => item.noShow),
          backgroundColor: "rgba(190, 24, 93, 0.72)",
        },
      ],
    };

    const serviceData = {
      labels: ["Infrared Sauna", "Red Light Therapy", "Massage"],
      datasets: [
        {
          data: [
            bookingData.summary.infraredBookings,
            bookingData.summary.redlightBookings,
            bookingData.summary.massageBookings,
          ],
          backgroundColor: [
            "rgba(54, 162, 235, 0.72)",
            "rgba(255, 99, 132, 0.72)",
            "rgba(180, 145, 97, 0.78)",
          ],
        },
      ],
    };

    return (
      <div className="chart-container">
        <div className="chart-row">
          <div className="chart-col">
            <Bar
              data={statusChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Live Booking Activity" },
                },
                scales: {
                  y: { beginAtZero: true, stacked: true },
                  x: { stacked: true },
                },
              }}
            />
          </div>
          <div className="chart-col">
            <Pie
              data={serviceData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Bookings by Service Type" },
                },
              }}
            />
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat-card">
            <h3>Total Bookings</h3>
            <p>{bookingData.summary.totalBookings}</p>
          </div>
          <div className="stat-card">
            <h3>Massage Bookings</h3>
            <p>{bookingData.summary.massageBookings}</p>
          </div>
          <div className="stat-card">
            <h3>Completion Rate</h3>
            <p>{bookingData.summary.completionRate.toFixed(1)}%</p>
          </div>
          <div className="stat-card">
            <h3>Session Net Revenue</h3>
            <p>{formatCurrency(bookingData.paymentSummary?.netRevenue)}</p>
          </div>
          <div className="stat-card">
            <h3>Massage Net Revenue</h3>
            <p>{formatCurrency(bookingData.paymentSummary?.massageNetRevenue)}</p>
          </div>
          <div className="stat-card">
            <h3>Refunded</h3>
            <p>{formatCurrency(bookingData.paymentSummary?.refundedRevenue)}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderMassagePayoutReport = () => {
    if (!massagePayoutData) return null;

    const providers = massagePayoutData.providers || [];

    if (providers.length === 0) {
      return (
        <div className="chart-container">
          <div className="empty-report-state">
            No completed massage sessions matched this range yet.
          </div>
        </div>
      );
    }

    const payoutChartData = {
      labels: providers.map((provider) => provider.providerName),
      datasets: [
        {
          label: "MT 60%",
          data: providers.map((provider) => provider.therapistShare),
          backgroundColor: "rgba(31, 92, 99, 0.72)",
        },
        {
          label: "DA 40%",
          data: providers.map((provider) => provider.studioShare),
          backgroundColor: "rgba(180, 145, 97, 0.78)",
        },
      ],
    };

    return (
      <div className="chart-container">
        <div className="chart-row">
          <div className="chart-col chart-col-wide">
            <Bar
              data={payoutChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Massage Payout Split by Therapist" },
                },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat-card">
            <h3>Completed Sessions</h3>
            <p>{massagePayoutData.summary.completedSessions}</p>
          </div>
          <div className="stat-card">
            <h3>Net Revenue</h3>
            <p>{formatCurrency(massagePayoutData.summary.netRevenue)}</p>
          </div>
          <div className="stat-card">
            <h3>MT 60%</h3>
            <p>{formatCurrency(massagePayoutData.summary.therapistShare)}</p>
          </div>
          <div className="stat-card">
            <h3>DA 40%</h3>
            <p>{formatCurrency(massagePayoutData.summary.studioShare)}</p>
          </div>
        </div>
        <div className="report-table-wrapper">
          <table className="report-table">
            <thead>
              <tr>
                <th>Therapist</th>
                <th>Completed</th>
                <th>60 Min</th>
                <th>90 Min</th>
                <th>Gross</th>
                <th>Refunded</th>
                <th>Net</th>
                <th>MT 60%</th>
                <th>DA 40%</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => (
                <tr key={provider.providerId || provider.providerName}>
                  <td>{provider.providerName}</td>
                  <td>{provider.completedSessions}</td>
                  <td>{provider.massage60Sessions}</td>
                  <td>{provider.massage90Sessions}</td>
                  <td>{formatCurrency(provider.grossRevenue)}</td>
                  <td>{formatCurrency(provider.refundedRevenue)}</td>
                  <td>{formatCurrency(provider.netRevenue)}</td>
                  <td>{formatCurrency(provider.therapistShare)}</td>
                  <td>{formatCurrency(provider.studioShare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCommunicationAnalyticsChart = () => {
    if (!communicationData || !communicationData.communications) return null;

    const chartData = {
      labels: communicationData.communications.map((item) => item.date),
      datasets: [
        {
          label: "Email",
          data: communicationData.communications.map((item) => item.email),
          backgroundColor: "rgba(54, 162, 235, 0.7)",
        },
        {
          label: "SMS",
          data: communicationData.communications.map((item) => item.sms),
          backgroundColor: "rgba(75, 192, 192, 0.7)",
        },
      ],
    };

    const engagementData = {
      labels: ["Opened", "Clicked", "No Engagement"],
      datasets: [
        {
          data: [
            communicationData.summary.totalOpened,
            communicationData.summary.totalClicked,
            communicationData.summary.totalEmails -
              communicationData.summary.totalOpened,
          ],
          backgroundColor: [
            "rgba(75, 192, 192, 0.7)",
            "rgba(54, 162, 235, 0.7)",
            "rgba(201, 203, 207, 0.7)",
          ],
        },
      ],
    };

    return (
      <div className="chart-container">
        <div className="chart-row">
          <div className="chart-col">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Communications Over Time" },
                },
                scales: {
                  y: { beginAtZero: true, stacked: true },
                  x: { stacked: true },
                },
              }}
            />
          </div>
          <div className="chart-col">
            <Pie
              data={engagementData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Email Engagement" },
                },
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderInteractionAnalyticsChart = () => {
    if (!interactionData || !interactionData.interactions) return null;

    const chartData = {
      labels: interactionData.interactions.map((item) => item.date),
      datasets: [
        {
          label: "Inbound",
          data: interactionData.interactions.map((item) => item.inbound),
          backgroundColor: "rgba(75, 192, 192, 0.7)",
        },
        {
          label: "Outbound",
          data: interactionData.interactions.map((item) => item.outbound),
          backgroundColor: "rgba(54, 162, 235, 0.7)",
        },
        {
          label: "Internal",
          data: interactionData.interactions.map((item) => item.internal),
          backgroundColor: "rgba(255, 206, 86, 0.7)",
        },
      ],
    };

    const typeData = {
      labels: Object.keys(interactionData.summary.typeBreakdown),
      datasets: [
        {
          data: Object.values(interactionData.summary.typeBreakdown),
          backgroundColor: [
            "rgba(54, 162, 235, 0.7)",
            "rgba(75, 192, 192, 0.7)",
            "rgba(255, 99, 132, 0.7)",
            "rgba(255, 206, 86, 0.7)",
            "rgba(153, 102, 255, 0.7)",
          ],
        },
      ],
    };

    return (
      <div className="chart-container">
        <div className="chart-row">
          <div className="chart-col">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: {
                    display: true,
                    text: "Customer Interactions Over Time",
                  },
                },
                scales: {
                  y: { beginAtZero: true, stacked: true },
                  x: { stacked: true },
                },
              }}
            />
          </div>
          <div className="chart-col">
            <Pie
              data={typeData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Interactions by Type" },
                },
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Loading analytics data...</div>;
    }

    if (error) {
      return <div className="error">{error}</div>;
    }

    switch (activeTab) {
      case "members":
        return renderMemberGrowthChart();
      case "accounts":
        return renderAccountAnalyticsChart();
      case "bookings":
        return renderBookingAnalyticsChart();
      case "massagePayouts":
        return renderMassagePayoutReport();
      case "communications":
        return renderCommunicationAnalyticsChart();
      case "interactions":
        return renderInteractionAnalyticsChart();
      default:
        return null;
    }
  };

  return (
    <StaffLayout>
      <div className="analytics-dashboard">
        <h1>Analytics Dashboard</h1>

        <div className="filters">
          <div className="filter-group">
            <label htmlFor="period">Period:</label>
            <select
              id="period"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>

          {activeTab === "massagePayouts" && (
            <div className="filter-group">
              <label htmlFor="providerId">Therapist:</label>
              <select
                id="providerId"
                value={providerId}
                onChange={(event) => setProviderId(event.target.value)}
              >
                <option value="">All therapists</option>
                {(massagePayoutData?.providerOptions || []).map((provider) => (
                  <option key={provider._id} value={provider._id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group export-group">
            <label htmlFor="exportType">Export:</label>
            <select
              id="exportType"
              value={exportType}
              onChange={(event) => setExportType(event.target.value)}
            >
              <option value="bookings">Bookings</option>
              <option value="accounts">Accounts</option>
              <option value="massagePayouts">Massage Payouts</option>
              <option value="members">Member Growth</option>
              <option value="communications">Communications</option>
              <option value="interactions">Customer Interactions</option>
            </select>
          </div>
          <button className="export-btn" onClick={handleExport}>
            Export CSV
          </button>
        </div>

        <div className="tabs">
          <button
            className={activeTab === "bookings" ? "active" : ""}
            onClick={() => setActiveTab("bookings")}
          >
            Bookings
          </button>
          <button
            className={activeTab === "accounts" ? "active" : ""}
            onClick={() => setActiveTab("accounts")}
          >
            Accounts
          </button>
          <button
            className={activeTab === "massagePayouts" ? "active" : ""}
            onClick={() => setActiveTab("massagePayouts")}
          >
            Massage Payouts
          </button>
          <button
            className={activeTab === "members" ? "active" : ""}
            onClick={() => setActiveTab("members")}
          >
            Member Growth
          </button>
          <button
            className={activeTab === "communications" ? "active" : ""}
            onClick={() => setActiveTab("communications")}
          >
            Communications
          </button>
          <button
            className={activeTab === "interactions" ? "active" : ""}
            onClick={() => setActiveTab("interactions")}
          >
            Customer Interactions
          </button>
        </div>

        <div className="content">{renderContent()}</div>
      </div>
    </StaffLayout>
  );
};

export default AnalyticsDashboard;
